import { getPageContext } from '@thoughtspot/blink-context';
import {
    allowedHostEvents,
    EventStatus,
    IncomingEventType,
    isWebView,
    shouldBypassValidation,
} from '@thoughtspot/embed-util';
import { cloneDeep } from 'lodash';
import {
    componentHandledEvents,
    EventHandler,
    logger,
    MessageType,
    NON_MEMOIZED_EVENTS,
    OnMessageEvent,
    rootLevelEvents,
    ValidationErrors,
} from './constants';
import { eventHandlerMap } from './subscribe';
import { computeHash, getHostEventConfig, getParentOrigin } from './utils';
import { dispatchValidationError, validateRequiredParams } from './validation';

// service worker events that are not handled by the event bridge
const serviceWorkerEvents = [
    'enable-service-worker',
    'disable-service-worker',
    'invalidate-info-cache',
];

// Wrapper to handle cases where feature flags/logger aren't available (e.g., in tests)
const safeIsWebView = (): boolean => {
    try {
        return isWebView();
    } catch {
        return false;
    }
};

// Global deduplication for Error events to prevent multiple charts from
// sending same error
const globalErrorCache = new Map<string, number>();

export const callHandler = async (
    handlers: Set<EventHandler>,
    context: string,
    payload: any,
    event?: OnMessageEvent,
) => {
    const hostEventConfig = getHostEventConfig();

    const handleSingleHandler = async (handler: EventHandler) => {
        const ctxParams = handler.requiredParams?.[context];
        try {
            // Validate params unless validation is bypassed or no schema exists
            const isValidationEnabled = !shouldBypassValidation(
                hostEventConfig,
            );
            const hasParamsToValidate = !!ctxParams;
            const shouldValidate = isValidationEnabled && hasParamsToValidate;
            if (shouldValidate) {
                const { isValid, errorMessage } = validateRequiredParams(
                    ctxParams,
                    payload,
                    '',
                    context,
                );
                if (!isValid) {
                    if (event?.ports?.[0]) {
                        event.ports[0].postMessage(
                            cloneDeep({
                                error: errorMessage,
                            }),
                        );
                    }
                    return;
                }
            }

            const data = await handler.callback(payload);
            // Only send response if handler returns data (matching original behavior)
            // Sending empty responses can break chart initialization
            if (data && event?.ports?.[0]) {
                event.ports[0].postMessage(cloneDeep(data));
            }
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : String(err);
            dispatchValidationError(
                ValidationErrors.handlerExecutionError(context, errorMessage),
            );

            if (event?.ports?.[0]) {
                event.ports[0].postMessage(
                    cloneDeep({
                        error: errorMessage,
                        context,
                        requiredParams: ctxParams,
                    }),
                );
            }
        }
    };

    // Process handlers sequentially to maintain order
    const handlerArray = Array.from(handlers);
    await handlerArray.reduce(
        (promise, handler) => promise.then(() => handleSingleHandler(handler)),
        Promise.resolve(),
    );
};

export const contextsMatch = (
    handlerContext: any,
    targetContext: any,
): boolean => {
    if (handlerContext === targetContext) return true;

    if (handlerContext === 'root') return false;

    if (typeof handlerContext === 'object' && handlerContext?.page) {
        return handlerContext.page === targetContext;
    }

    return false;
};

/**
 * Checks if an event type should be handled by a component directly
 * rather than through the event bridge subscription system.
 * Component-handled events bypass the bridge and are forwarded to component listeners.
 *
 * @param eventType - The event type to check
 * @param context - The context to check
 * @returns True if the event should be handled by a component
 */
export const isComponentHandledEvent = (eventType: string): boolean => {
    return componentHandledEvents.has(eventType);
};

/**
 * Skip dispatching validation errors for unknown/unrecognized event types.
 * Only recognized events (present in allowedHostEvents values) that have
 * no registered handler should trigger a validation error.
 */
export const skipErrorDispatch = (type: string): boolean => {
    const isAllowed = Object.values(allowedHostEvents).includes(type as IncomingEventType);
    // skip for service worker events for playground
    const isServiceWorkerEvent = serviceWorkerEvents.includes(type);
    return !type || !isAllowed || isServiceWorkerEvent;
};

export const onMessage = async (event: OnMessageEvent) => {
    const hostEventConfig = getHostEventConfig();
    const isHostEventsV2Enabled = hostEventConfig.useHostEventsV2;
    const { type, context: passedContext, data } = event.data ?? {};

    const pageContext = getPageContext();
    const context = passedContext || pageContext?.page;

    const handlers = eventHandlerMap.get(type);

    // Check if this event should be handled by a component directly
    // Component-handled events bypass the event bridge and are forwarded to component listeners
    if (isComponentHandledEvent(type)) {
        if (context && context !== componentHandledEvents.get(type)) {
            dispatchValidationError(
                ValidationErrors.contextMismatch(
                    type,
                    componentHandledEvents.get(type),
                    context,
                ),
            );
            return;
        }

        // Don't intercept - the component's message listener will handle it
        // The event bridge's onMessage is registered on window, and the component also
        // listens on window, so both will receive it. We just need to not block it here.
        // Return early without responding, let the component handle it and respond via port
        return;
    }
    if (!handlers || handlers.size === 0) {
        if (!skipErrorDispatch(type)) {
            dispatchValidationError(
                ValidationErrors.noHandlerRegistered(context),
            );
        }
        return;
    }

    if (rootLevelEvents.has(type as IncomingEventType)) {
        const handlerArray = Array.from(handlers);
        await handlerArray.reduce(async (promise, handler) => {
            await promise;
            let results: any;
            try {
                const isValidationEnabled = !shouldBypassValidation(
                    hostEventConfig,
                );
                const hasParamsToValidate = !!handler.requiredParams?.[context];
                if (isValidationEnabled && hasParamsToValidate) {
                    const { isValid } = validateRequiredParams(
                        handler.requiredParams?.[context],
                        data,
                        '',
                        context,
                    );
                    if (!isValid) {
                        return;
                    }
                }
                results = await handler.callback(data, context);
            } catch (err) {
                results = { error: err };
            }
            // Only send response if handler returns data (matching original behavior)
            if (results && event?.ports?.[0]) {
                event.ports[0].postMessage(cloneDeep(results));
            }
        }, Promise.resolve());
        return;
    }

    let matching: EventHandler[] = [];
    if (!isHostEventsV2Enabled) {
        matching = Array.from(handlers);
    } else {
        matching = Array.from(handlers).filter(handler =>
            contextsMatch(handler.context, context),
        );
    }

    if (matching.length === 0) {
        dispatchValidationError(ValidationErrors.noMatchingHandler(context));
        return;
    }

    await callHandler(new Set(matching), context, data, event);
};

export const destroy = () => {
    window.removeEventListener('message', onMessage);
};

export const dispatchEvent = (nonMemoizedEventTypes?: Set<string>) => {
    const memo = new Map<MessageType, string>();
    return (type: MessageType, payload: any, status = EventStatus.End) => {
        if (window === window.parent && !safeIsWebView()) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            const channel = new window.MessageChannel();
            channel.port1.onmessage = ({ data }) => {
                channel.port1.close();
                if (data.error) {
                    reject(data.error);
                } else {
                    resolve(data);
                }
            };

            if (safeIsWebView()) {
                const listener = (event: any) => {
                    if (event.data.type === type) {
                        resolve(event.data || {});
                    } else {
                        reject(event.data || {});
                    }
                    window.removeEventListener('message', listener);
                };
                window.addEventListener('message', listener);
            }

            let dispatchRepeat = nonMemoizedEventTypes?.has(type);
            if (!dispatchRepeat) {
                const hash = computeHash(payload);
                const prevHash = memo.get(type);

                dispatchRepeat = !prevHash || hash !== prevHash;
                if (dispatchRepeat) {
                    memo.set(type, hash);
                }
            }

            // Global deduplication for Error events across all instances
            if (type === 'Error' && dispatchRepeat) {
                const errorHash = computeHash(payload);
                const lastTime = globalErrorCache.get(errorHash);
                const now = Date.now();
                // Only dispatch if not sent in last 100ms
                if (lastTime && now - lastTime < 100) {
                    dispatchRepeat = false;
                } else {
                    globalErrorCache.set(errorHash, now);
                }
            }

            const parentOrigin = getParentOrigin();

            try {
                if (dispatchRepeat && parentOrigin !== null) {
                    if (safeIsWebView()) {
                        (window as any).ReactNativeWebView?.postMessage(
                            JSON.stringify({
                                type,
                                data: cloneDeep(payload),
                                status,
                            }),
                        );
                        resolve({});
                    } else {
                        window.parent.postMessage(
                            { type, data: cloneDeep(payload), status },
                            parentOrigin,
                            [channel.port2],
                        );
                    }
                }
            } catch (error) {
                logger.error('Error dispatching event', error);
            }
        });
    };
};

export const dispatchNonMemoEvent = dispatchEvent(new Set(NON_MEMOIZED_EVENTS));
